import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, KeyRound, Lock, ArrowLeft } from 'lucide-react';
import { authService } from '../services/authService';

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const emailFromQuery = searchParams.get('email') ?? '';

    const [email, setEmail] = useState(emailFromQuery);
    const [otpCode, setOtpCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setErrorMessage('');

        if (otpCode.length !== 6) {
            setErrorMessage('El código debe tener 6 dígitos.');
            return;
        }

        if (newPassword.length < 6) {
            setErrorMessage('La contraseña debe tener al menos 6 caracteres.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setErrorMessage('Las contraseñas no coinciden.');
            return;
        }

        setIsLoading(true);

        try {
            await authService.resetPassword(email, otpCode, newPassword);
            navigate('/login', {
                replace: true,
                state: { message: 'Contraseña restablecida correctamente. Ya puedes iniciar sesión.' },
            });
        } catch (err: any) {
            setErrorMessage(err.message || 'Error al restablecer la contraseña.');
            setIsLoading(false);
        }
    };

    return (
        <div className="mx-auto flex min-h-screen max-w-md flex-col bg-white shadow-xl">
            <div className="flex flex-1 flex-col justify-center px-6 py-12">
                <div className="mb-10 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-100">
                        <KeyRound size={32} className="text-indigo-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        Nueva contraseña
                    </h1>
                    <p className="mt-2 text-sm text-gray-500">
                        Ingresa el código que recibiste y tu nueva contraseña.
                    </p>
                </div>

                <div className={`mb-6 rounded-2xl bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-600 transition-all ${errorMessage ? 'block' : 'hidden'}`}>
                    {errorMessage || '\u00A0'}
                </div>

                <form onSubmit={handleSubmit} translate="no" className="flex flex-col gap-4">
                    {/* Email (pre-filled if coming from ForgotPassword) */}
                    <div className={`${!emailFromQuery ? 'block' : 'hidden'}`}>
                        <label
                            htmlFor="reset-email"
                            className="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Correo electrónico
                        </label>
                        <input
                            id="reset-email"
                            type="email"
                            required={!emailFromQuery}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="tu@correo.com"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3.5 px-4 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                        />
                    </div>

                    {/* OTP Code */}
                    <div>
                        <label
                            htmlFor="otp-code"
                            className="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Código de recuperación
                        </label>
                        <input
                            id="otp-code"
                            type="text"
                            inputMode="numeric"
                            required
                            maxLength={6}
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                            placeholder="000000"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3.5 px-4 text-center font-mono text-xl tracking-[0.4em] text-gray-900 outline-none transition-all placeholder:text-gray-400 placeholder:tracking-[0.4em] focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                        />
                    </div>

                    {/* New Password */}
                    <div>
                        <label
                            htmlFor="new-password"
                            className="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Nueva contraseña
                        </label>
                        <div className="relative">
                            <Lock
                                size={18}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                id="new-password"
                                type="password"
                                required
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Mínimo 6 caracteres"
                                className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3.5 pl-11 pr-4 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                            />
                        </div>
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label
                            htmlFor="confirm-password"
                            className="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Confirmar contraseña
                        </label>
                        <div className="relative">
                            <Lock
                                size={18}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                id="confirm-password"
                                type="password"
                                required
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Repite la contraseña"
                                className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3.5 pl-11 pr-4 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                            />
                        </div>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={isLoading || otpCode.length !== 6 || !newPassword || !confirmPassword}
                        className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl bg-indigo-600 py-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition-all hover:bg-indigo-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                <span>Restableciendo...</span>
                            </>
                        ) : (
                            <span>Restablecer contraseña</span>
                        )}
                    </button>
                </form>

                <Link
                    to="/login"
                    className="mt-6 inline-flex items-center justify-center gap-1 text-center text-sm font-medium text-indigo-600 hover:text-indigo-700"
                >
                    <ArrowLeft size={16} />
                    Volver al login
                </Link>
            </div>
        </div>
    );
}
